import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { FiSearch, FiX, FiChevronDown, FiCheck } from "react-icons/fi";

export default function NotesAndCommentaryExport({ contextPath }) {
    const [vendors, setVendors] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [fetching, setFetching] = useState(false);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
    };

    useEffect(() => {
        let mounted = true;
        setFetching(true);
        axios
            .get(`${contextPath}/exports/vendors-for-notes`, { headers })
            .then((res) => mounted && setVendors(res.data || []))
            .catch(() => mounted && setErr("Failed to load vendors"))
            .finally(() => mounted && setFetching(false));
        return () => {
            mounted = false;
        };
    }, [contextPath]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (selected.length === 0) {
            setErr("Please select at least one vendor.");
            return;
        }
        setLoading(true);
        try {
            const resp = await axios.post(
                `${contextPath}/exports/notes-commentary`,
                { vendorIds: selected.map(Number) },
                { responseType: "blob", headers }
            );

            // derive filename from Content-Disposition
            const cd = resp.headers["content-disposition"] || "";
            const match =
                /filename\*=UTF-8''([^;]+)/i.exec(cd) ||
                /filename="?([^"]+)"?/i.exec(cd);
            const filename = match ? decodeURIComponent(match[1]) : "Notes_and_Commentary.pdf";

            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (ex) {
            setErr("Export failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-3">
            <div className="text-[16px] font-bold">NOTES &amp; COMMENTARY REPORT</div>

            <SearchableMultiSelect
                options={vendors}
                value={selected}
                onChange={setSelected}
                placeholder={fetching ? "Loading vendors..." : "Search vendors..."}
                disabled={fetching}
            />

            <div className="flex items-center gap-2">
                <button
                    type="submit"
                    disabled={loading || selected.length === 0}
                    className={`px-4 py-2 rounded text-white ${selected.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
                    style={{ backgroundColor: "rgb(232, 119, 34)" }} // rusty orange
                    title="Use a PDF reader to open the downloaded PDF file for optimal contents rendering"
                >
                    {loading ? "Exporting..." : "Export to PDF"}
                </button>
                {err ? <span className="text-red-600 text-sm">{err}</span> : null}
            </div>
        </form>
    );
}

/* ================= Searchable Multi-Select ================= */

function SearchableMultiSelect({
                                   options = [],           // [{id, name}]
                                   value = [],             // ["id","id2"]
                                   onChange,
                                   placeholder = "Search...",
                                   disabled = false,
                               }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(0);
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    const normalized = useMemo(
        () => (options || []).map((o) => ({ id: String(o.id), name: String(o.name || "") })),
        [options]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return normalized;
        return normalized.filter(
            (v) => v.name.toLowerCase().includes(q) || v.id.toLowerCase().includes(q)
        );
    }, [normalized, query]);

    const isSelected = useCallback((id) => value.includes(id), [value]);

    const toggle = (id) => {
        if (isSelected(id)) {
            onChange(value.filter((v) => v !== id));
        } else {
            onChange([...value, id]);
        }
    };

    const clearAll = () => onChange([]);

    useEffect(() => {
        function onDocClick(e) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const onKeyDown = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const item = filtered[highlight];
            if (item) toggle(item.id);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    // Chips (limit display if too many)
    const selectedItems = useMemo(() => {
        const map = new Map(normalized.map((n) => [n.id, n.name]));
        const list = value.map((id) => ({ id, name: map.get(id) || id }));
        return list;
    }, [value, normalized]);

    const CHIP_LIMIT = 3;
    const chipOverflow = Math.max(selectedItems.length - CHIP_LIMIT, 0);

    return (
        <div className="relative" ref={rootRef} onKeyDown={onKeyDown}>
            <button
                type="button"
                className={`w-full border rounded px-2 py-2 text-left flex items-center justify-between ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={() => !disabled && setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {selectedItems.length === 0 ? (
                        <span className="text-gray-500">{placeholder}</span>
                    ) : (
                        <>
                            {selectedItems.slice(0, CHIP_LIMIT).map((it) => (
                                <span
                                    key={it.id}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100"
                                >
                  {it.name}
                                    <button
                                        type="button"
                                        className="p-0.5 -mr-1 hover:text-red-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggle(it.id);
                                        }}
                                        aria-label={`Remove ${it.name}`}
                                        title="Remove"
                                    >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
                            ))}
                            {chipOverflow > 0 && (
                                <span className="text-xs text-gray-600">+{chipOverflow} more</span>
                            )}
                        </>
                    )}
                </div>
                <FiChevronDown className="w-4 h-4 text-[#25408f]" />
            </button>

            {open && (
                <div
                    className="absolute z-20 mt-1 w-full bg-white border rounded shadow-lg"
                    role="listbox"
                    aria-label="Vendors"
                >
                    {/* Search bar */}
                    <div className="p-2 border-b">
                        <div className="flex items-center gap-2 rounded border px-2">
                            <FiSearch className="w-4 h-4 text-[#25408f]" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setHighlight(0);
                                }}
                                className="w-full outline-none py-1 text-sm"
                                placeholder={placeholder}
                            />
                            {query && (
                                <button
                                    type="button"
                                    className="p-1 text-gray-500 hover:text-gray-800"
                                    onClick={() => {
                                        setQuery("");
                                        setHighlight(0);
                                        inputRef.current?.focus();
                                    }}
                                    aria-label="Clear search"
                                    title="Clear"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                            <button
                                type="button"
                                className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                                onClick={clearAll}
                            >
                                Clear
                            </button>
                            <span className="ml-auto text-xs text-gray-500">
                {filtered.length} / {normalized.length}
              </span>
                        </div>
                    </div>

                    {/* Options */}
                    <ul className="max-h-64 overflow-auto">
                        {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-gray-500">No vendors found</li>
                        ) : (
                            filtered.map((opt, idx) => {
                                const active = idx === highlight;
                                const selected = isSelected(opt.id);
                                return (
                                    <li
                                        key={opt.id}
                                        role="option"
                                        aria-selected={selected}
                                        className={`px-3 py-2 text-sm flex items-center justify-between cursor-pointer
                      ${active ? "bg-gray-100" : ""}
                      ${selected ? "font-medium" : ""}`}
                                        onMouseEnter={() => setHighlight(idx)}
                                        onClick={() => toggle(opt.id)}
                                    >
                                        <span className="truncate">{opt.name}</span>
                                        {selected && <FiCheck className="w-4 h-4 text-[#25408f]" />}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
